import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageSquare, Send, ArrowLeft, Home, Building2, User } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export function MessagingCenter() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Fetch conversations
  const { data: conversations } = useQuery({
    queryKey: ['conversations', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('conversations')
        .select(`
          *,
          properties(title, city),
          projects(name, city)
        `)
        .order('last_message_at', { ascending: false });
      return data || [];
    },
    enabled: !!user,
  });

  // Fetch messages for selected conversation
  const { data: messages } = useQuery({
    queryKey: ['messages', selectedConversation],
    queryFn: async () => {
      const { data } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', selectedConversation!)
        .order('created_at', { ascending: true });
      return data || [];
    },
    enabled: !!selectedConversation,
  });

  // Fetch other user profiles for conversations
  const { data: profiles } = useQuery({
    queryKey: ['conversation-profiles', conversations?.map(c => c.participant_one === user?.id ? c.participant_two : c.participant_one)],
    queryFn: async () => {
      const otherIds = conversations?.map(c => c.participant_one === user?.id ? c.participant_two : c.participant_one) || [];
      if (otherIds.length === 0) return [];
      const { data } = await supabase.from('profiles').select('user_id, full_name, avatar_url').in('user_id', otherIds);
      return data || [];
    },
    enabled: !!conversations && conversations.length > 0,
  });

  // Send message mutation
  const sendMessage = useMutation({
    mutationFn: async () => {
      if (!newMessage.trim() || !selectedConversation) return;
      await supabase.from('messages').insert({
        conversation_id: selectedConversation,
        sender_id: user!.id,
        content: newMessage.trim(),
      });
      await supabase.from('conversations').update({ last_message_at: new Date().toISOString() }).eq('id', selectedConversation);
    },
    onSuccess: () => {
      setNewMessage('');
      queryClient.invalidateQueries({ queryKey: ['messages', selectedConversation] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  // Mark messages as read
  useEffect(() => {
    if (selectedConversation && messages) {
      const unreadIds = messages.filter(m => !m.is_read && m.sender_id !== user?.id).map(m => m.id);
      if (unreadIds.length > 0) {
        supabase.from('messages').update({ is_read: true }).in('id', unreadIds).then(() => {
          queryClient.invalidateQueries({ queryKey: ['messages', selectedConversation] });
        });
      }
    }
  }, [selectedConversation, messages]);

  // Realtime subscription
  useEffect(() => {
    if (!selectedConversation) return;
    const channel = supabase
      .channel(`messages-${selectedConversation}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${selectedConversation}` }, () => {
        queryClient.invalidateQueries({ queryKey: ['messages', selectedConversation] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [selectedConversation]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getProfileName = (userId: string) => {
    return profiles?.find(p => p.user_id === userId)?.full_name || 'User';
  };

  const getConversationTitle = (conv: any) => {
    const propTitle = conv.properties?.title
      ? (typeof conv.properties.title === 'object' ? (conv.properties.title as any)?.en : conv.properties.title)
      : null;
    const projName = conv.projects?.name
      ? (typeof conv.projects.name === 'object' ? (conv.projects.name as any)?.en : conv.projects.name)
      : null;
    return propTitle || projName || 'General';
  };

  const getUnreadCount = (convId: string) => {
    // We'd need a separate query for this, simplified for now
    return 0;
  };

  if (selectedConversation) {
    const conv = conversations?.find(c => c.id === selectedConversation);
    const otherId = conv ? (conv.participant_one === user?.id ? conv.participant_two : conv.participant_one) : '';

    return (
      <div className="flex flex-col h-[calc(100vh-140px)]">
        {/* Chat Header */}
        <div className="flex items-center gap-3 p-4 border-b bg-card rounded-t-2xl">
          <Button variant="ghost" size="icon" onClick={() => setSelectedConversation(null)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="font-semibold text-sm">{getProfileName(otherId)}</div>
            <div className="text-xs text-muted-foreground">
              {conv && getConversationTitle(conv)} · {conv?.properties?.city || conv?.projects?.city || ''}
            </div>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4 bg-muted/30">
          <div className="space-y-3 max-w-3xl mx-auto">
            {messages?.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p>No messages yet. Start the conversation!</p>
              </div>
            )}
            {messages?.map((msg) => {
              const isMine = msg.sender_id === user?.id;
              return (
                <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${isMine ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-card border rounded-bl-md'}`}>
                    <p className="text-sm">{msg.content}</p>
                    <div className={`text-[10px] mt-1 ${isMine ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                      {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                      {isMine && msg.is_read && ' · Read'}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Message Input */}
        <div className="p-4 border-t bg-card rounded-b-2xl">
          <form onSubmit={(e) => { e.preventDefault(); sendMessage.mutate(); }} className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 rounded-xl"
            />
            <Button type="submit" disabled={!newMessage.trim() || sendMessage.isPending} className="rounded-xl gap-2">
              <Send className="h-4 w-4" /> Send
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          Messages
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Chat with agents, developers, and property owners.</p>
      </div>

      {!conversations || conversations.length === 0 ? (
        <Card className="rounded-2xl">
          <CardContent className="p-12 text-center">
            <MessageSquare className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground font-medium">No conversations yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              When you contact a property owner or agent, your conversations will appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {conversations.map((conv: any) => {
            const otherId = conv.participant_one === user?.id ? conv.participant_two : conv.participant_one;
            const title = getConversationTitle(conv);
            const city = conv.properties?.city || conv.projects?.city || '';

            return (
              <Card
                key={conv.id}
                className="rounded-xl cursor-pointer hover:shadow-sm transition-all"
                onClick={() => setSelectedConversation(conv.id)}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{getProfileName(otherId)}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      {conv.property_id ? <Home className="h-3 w-3" /> : <Building2 className="h-3 w-3" />}
                      {title} {city && `· ${city}`}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0">
                    {formatDistanceToNow(new Date(conv.last_message_at), { addSuffix: true })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
